<template>
  <v-container fluid>
    <div v-if="routePlan.id">
      <customer-context-prompt :resource="routePlan"/>
      <dialog-confirm ref="delete-dialog">
        {{ $t('delete_route_plan_confirm') }}
      </dialog-confirm>
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <h2>{{ $tc('route_plan') }}: {{ routePlan.route_number }}</h2>
          <div class="d-flex align-center mb-2 ml-4">
            <v-chip small>
              <multi-time-display :time="routePlan.start"/>
            </v-chip>
            <span class="mx-2"> - </span>
            <v-chip small>
              <multi-time-display :time="routePlan.stop"/>
            </v-chip>
          </div>
        </div>
        <div class="d-flex">
          <div v-if="routes.length === 0">
            <v-btn
              class="ml-2"
              outlined
              color="primary"
              :to="createRouteUrl">
              <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
              {{ $t('create_route' )}}
            </v-btn>
          </div>
          <div v-if="user.can('delete_routeplan')" class="ml-2">
            <v-btn
              color="danger"
              outlined
              @click="deleteRoutePlan"
              :loading="deleting">
              <v-icon class="mr-1">mdi-delete</v-icon>
              {{ $t('delete_route_plan') }}
            </v-btn>
          </div>
        </div>
      </div>
      <v-row>
        <v-col>
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <h6 class="d-flex">
                <v-btn fab small elevation="0" color="std_gray" class="mr-2">
                  <v-icon>mdi-calendar-check</v-icon>
                </v-btn>
                <span class="d-flex flex-column">
                  <span>{{ $tc('stop', 2) }}</span>
                  <span class="text-caption">{{ $t('route_plan_view_ui.stops_table') }}</span>
                </span>
              </h6>
            </v-card-title>
            <v-card-text class="mt-n2">
              <v-data-table
                hide-default-footer
                disable-pagination
                class="vf-table mt-2"
                :headers="stopTableFields"
                :items="routePlan.route_plan_locations">
                <template #item.location="row">
                  <!-- location is present, show link to it -->
                  <router-link
                    v-if="row.item.location && user.can('update_location')"
                    :to="`/location/update/${row.item.location.id}`">
                    <span v-if="row.item.location.type === 'dc'">
                      <v-icon color="primary" >mdi-office-building</v-icon>
                    </span>
                    <span v-else>
                      <v-avatar color="primary" size="25">
                        <span class="white--text">{{ row.index }}</span>
                      </v-avatar>
                    </span>
                    <span class="ml-3">{{ row.value.label }}</span>
                  </router-link>
                  <!-- unregistered location -->
                  <div v-else class="d-flex align-center">
                    <vf-tooltip :text="$tc('unregistered_location')">
                      <v-avatar color="warning" size="25">
                        <span class="white--text">{{ row.index }}</span>
                      </v-avatar>
                      <span class="ml-3">{{ row.item.location_code }}</span>
                    </vf-tooltip>
                  </div>
                </template>
                <template #item.eta="row">
                  <multi-time-display :time="row.value" />
                </template>
                <template #item.edit="row">
                  <vf-tooltip v-if="row.item.location === null" :text="$t('create_new_location_from_code')">
                    <v-btn fab text small :to="`/location/create?unregistered=1&code=${row.item.location_code}`">
                      <v-icon color="primary">mdi-plus-circle</v-icon>
                    </v-btn>
                  </vf-tooltip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <h6 class="d-flex">
                <v-btn fab small elevation="0" color="std_gray" class="mr-2">
                  <v-icon>mdi-map-marker-path</v-icon>
                </v-btn>
                <span class="d-flex flex-column">
                  <span>{{ $tc('route', 2) }}</span>
                  <span class="text-caption">{{ $t('route_plan_view_ui.routes_table') }}</span>
                </span>
              </h6>
            </v-card-title>
            <v-card-text class="mt-n2">
              <v-data-table
                hide-default-footer
                class="vf-table mt-2"
                :headers="routeTableFields"
                :items="routes" >
                <template #item.name="row">
                  <router-link :to="'/route/' + row.item.id" >{{ row.value }}</router-link>
                </template>
                <template #item.vehicle="row">
                  <router-link :to="'/vehicle/' + row.value.id" ><v-icon>mdi-truck</v-icon> {{ row.value.name }}</router-link>
                </template>
                <template #item.start="row">
                  <multi-time-display :time="row.value" />
                </template>
                <template #item.stop="row">
                  <multi-time-display :time="row.value" />
                </template>
                <template #item.duration="row">
                  {{ row.value | humanizeDuration(({ numberOfSignificantParts: 2, separator: ', ' })) }}
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import CustomerContextPrompt from '@/components/shared/CustomerContextPrompt'
import DialogConfirm from '@/components/shared/DialogConfirm'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import VfTooltip from '@/components/shared/VfTooltip'
import { map, isEmpty } from 'lodash'

export default {
  name: 'RoutePlanView',
  components: {
    CustomerContextPrompt,
    DialogConfirm,
    MultiTimeDisplay,
    VfTooltip
  },
  mixins: [
    CurrentUserMixin,
    DateTimeUtilsMixin
  ],
  data () {
    return {
      isEmpty: isEmpty,
      routePlan: {},
      routes: [],
      deleting: false,
      stopTableFields: [
        { value: 'location', text: this.$tc('location', 2), sortable: false },
        { value: 'eta', text: this.$t('arrival'), sortable: false },
        { value: 'edit', text: '', sortable: false }
      ],
      routeTableFields: [
        { value: 'name', text: this.$t('name'), sortable: false },
        { value: 'vehicle', text: this.$tc('vehicle', 2), sortable: false },
        { value: 'start', text: this.$t('start'), sortable: false },
        { value: 'stop', text: this.$t('end'), sortable: false },
        { value: 'duration', text: this.$t('duration'), sortable: false }
      ]
    }
  },
  computed: {
    createRouteUrl () {
      return `/route/create/${this.routePlan.id}`;
    }
  },
  methods: {
    /**
     * Fetch the route plan associated with the router param
     */
    fetchRoutePlan () {
      const routePlanId = this.$route.params.id

      const fields = [
        'id',
        'route_number',
        'start',
        'stop',
        'customer_id',
        'route_plan_locations.id',
        'route_plan_locations.eta',
        'route_plan_locations.location_code',
        'route_plan_locations.location.id',
        'route_plan_locations.location.label',
        'route_plan_locations.location.type'
      ]

      const params = {
        expand: 'route_plan_locations.location',
        fields: fields.join(',')
      }
      this.$orm('RoutePlan')
        .find(routePlanId, { params: params })
        .then(routePlan => {
          this.routePlan = routePlan
          this.fetchRoutes()
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    /**
     * Fetch Routes associated with the current route plan.
     */
    fetchRoutes () {
      const params = {
        expand: 'vehicle',
        route_plan_id: this.routePlan.id,
        customer_id: map(this.customers, x => x.id).join(',')
      }
      this.$orm('Route')
        .findAll(params)
        .then(resp => {
          this.routes = resp.items
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    /**
     * Delete the route plan after confirmation
     */
    async deleteRoutePlan () {
      if (await this.$refs['delete-dialog'].confirm()) {
        this.deleting = true
        this.$gtag.event('delete', { event_category: 'RoutePlan' })
        try {
          await this.routePlan.destroy()
          this.$router.push('/route-plans', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$tc('route_plan')} ${this.routePlan.route_number} ${this.$t('generic_delete_success')}`
            )
          })
        } catch (error) {
          this.handleError(error)
        } finally {
          this.deleting = false
        }
      }
    }
  },
  created () {
    this.fetchRoutePlan()
  }
}

</script>
